import clsx from 'clsx';
import type { ElementType } from 'react';

type KpiVariant = 'highlight' | 'default' | 'critical';

interface KpiTileProps {
	label: string;
	value: number | string;
	subtext: string;
	icon: ElementType;
	variant?: KpiVariant;
	onClick?: () => void;
	active?: boolean;
	tourId?: string;
}

const CONTAINER: Record<KpiVariant, string> = {
	highlight: 'bg-gradient-to-br from-primary-500 to-primary-700 border-transparent text-white shadow-raro',
	default: 'bg-white border-raro-gray-200 text-raro-gray-900',
	critical: 'bg-white border-red-200 text-raro-gray-900',
};

const ICON_BOX: Record<KpiVariant, string> = {
	highlight: 'bg-white/20 text-white',
	default: 'bg-primary-100 text-primary-600',
	critical: 'bg-red-100 text-red-600',
};

const LABEL: Record<KpiVariant, string> = {
	highlight: 'text-white/80',
	default: 'text-raro-gray-500',
	critical: 'text-raro-gray-500',
};

const SUBTEXT: Record<KpiVariant, string> = {
	highlight: 'text-white/80',
	default: 'text-raro-gray-500',
	critical: 'text-red-600',
};

export function KpiTile({ label, value, subtext, icon: Icon, variant = 'default', onClick, active = false, tourId }: KpiTileProps) {
	const Component = onClick ? 'button' : 'div';

	return (
		<Component
			onClick={onClick}
			aria-pressed={onClick ? active : undefined}
			data-tour={tourId}
			className={clsx(
				'rounded-2xl border p-4 md:p-5 h-full w-full text-left',
				CONTAINER[variant],
				onClick && 'transition-all hover:shadow-md active:scale-[0.98] focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary-500',
				active && onClick && 'ring-2 ring-offset-2 ring-primary-500'
			)}
		>
			<div className="flex items-start justify-between gap-3">
				<p className={clsx('text-xs font-medium', LABEL[variant])}>{label}</p>
				<span className={clsx('flex h-8 w-8 md:h-10 md:w-10 shrink-0 items-center justify-center rounded-full', ICON_BOX[variant])}>
					<Icon className="h-5 w-5" aria-hidden="true" />
				</span>
			</div>
			<p className="mt-2 text-2xl md:text-3xl font-bold font-montserrat">{value}</p>
			<p className={clsx('mt-1 text-xs line-clamp-2', SUBTEXT[variant])}>{subtext}</p>
		</Component>
	);
}
